import { useEffect, useMemo, useState } from 'react'
import type { CustomerPrivacyDashboard } from '../../customerPrivacyApi'

type PrivacyProfileCardProps = {
  title: string
  description: string
  profile: CustomerPrivacyDashboard['profile']
  firstNameLabel: string
  lastNameLabel: string
  emailLabel: string
  phoneLabel: string
  residentialAddressLabel: string
  billingAddressLabel: string
  shippingAddressLabel: string
  saveLabel: string
  savingLabel: string
  onSave: (input: {
    firstName?: string
    lastName?: string
    email?: string
    phone?: string
    residentialAddress?: string
    billingAddress?: string
    shippingAddress?: string
  }) => Promise<boolean>
}

export const PrivacyProfileCard = ({
  title,
  description,
  profile,
  firstNameLabel,
  lastNameLabel,
  emailLabel,
  phoneLabel,
  residentialAddressLabel,
  billingAddressLabel,
  shippingAddressLabel,
  saveLabel,
  savingLabel,
  onSave,
}: PrivacyProfileCardProps) => {
  const [firstName, setFirstName] = useState(profile.firstName || '')
  const [lastName, setLastName] = useState(profile.lastName || '')
  const [email, setEmail] = useState(profile.email || '')
  const [phone, setPhone] = useState(profile.phone || '')
  const [residentialAddress, setResidentialAddress] = useState(profile.residentialAddress || '')
  const [billingAddress, setBillingAddress] = useState(profile.billingAddress || '')
  const [shippingAddress, setShippingAddress] = useState(profile.shippingAddress || '')
  const [saving, setSaving] = useState(false)
  const [feedback, setFeedback] = useState<'saved' | 'error' | null>(null)

  useEffect(() => {
    setFirstName(profile.firstName || '')
    setLastName(profile.lastName || '')
    setEmail(profile.email || '')
    setPhone(profile.phone || '')
    setResidentialAddress(profile.residentialAddress || '')
    setBillingAddress(profile.billingAddress || '')
    setShippingAddress(profile.shippingAddress || '')
  }, [profile])

  const changed = useMemo(
    () =>
      firstName.trim() !== (profile.firstName || '').trim() ||
      lastName.trim() !== (profile.lastName || '').trim() ||
      email.trim() !== (profile.email || '').trim() ||
      phone.trim() !== (profile.phone || '').trim() ||
      residentialAddress.trim() !== (profile.residentialAddress || '').trim() ||
      billingAddress.trim() !== (profile.billingAddress || '').trim() ||
      shippingAddress.trim() !== (profile.shippingAddress || '').trim(),
    [
      billingAddress,
      email,
      firstName,
      lastName,
      phone,
      profile.billingAddress,
      profile.email,
      profile.firstName,
      profile.lastName,
      profile.phone,
      profile.residentialAddress,
      profile.shippingAddress,
      residentialAddress,
      shippingAddress,
    ],
  )

  const handleSubmit = async () => {
    setSaving(true)
    setFeedback(null)
    try {
      const ok = await onSave({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        residentialAddress: residentialAddress.trim(),
        billingAddress: billingAddress.trim(),
        shippingAddress: shippingAddress.trim(),
      })
      setFeedback(ok ? 'saved' : 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className="ca-card-shell ca-theme-transition rounded-3xl p-5">
      <h2 className="text-sm font-semibold uppercase tracking-[0.16em] text-(--color-text-muted)">{title}</h2>
      <p className="mt-1 text-sm text-(--color-text-secondary)">{description}</p>
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        <label className="text-sm text-(--color-text-secondary)">
          <span className="mb-1 block">{firstNameLabel}</span>
          <input
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
            className="ca-input w-full px-3 py-2 text-sm"
          />
        </label>
        <label className="text-sm text-(--color-text-secondary)">
          <span className="mb-1 block">{lastNameLabel}</span>
          <input
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
            className="ca-input w-full px-3 py-2 text-sm"
          />
        </label>
        <label className="text-sm text-(--color-text-secondary)">
          <span className="mb-1 block">{emailLabel}</span>
          <input
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            className="ca-input w-full px-3 py-2 text-sm"
          />
        </label>
        <label className="text-sm text-(--color-text-secondary)">
          <span className="mb-1 block">{phoneLabel}</span>
          <input
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            className="ca-input w-full px-3 py-2 text-sm"
          />
        </label>
      </div>
      <div className="mt-3 grid gap-3">
        <label className="text-sm text-(--color-text-secondary)">
          <span className="mb-1 block">{residentialAddressLabel}</span>
          <input
            value={residentialAddress}
            onChange={(event) => setResidentialAddress(event.target.value)}
            className="ca-input w-full px-3 py-2 text-sm"
          />
        </label>
        <div className="grid gap-3 md:grid-cols-2">
          <label className="text-sm text-(--color-text-secondary)">
            <span className="mb-1 block">{billingAddressLabel}</span>
            <input
              value={billingAddress}
              onChange={(event) => setBillingAddress(event.target.value)}
              className="ca-input w-full px-3 py-2 text-sm"
            />
          </label>
          <label className="text-sm text-(--color-text-secondary)">
            <span className="mb-1 block">{shippingAddressLabel}</span>
            <input
              value={shippingAddress}
              onChange={(event) => setShippingAddress(event.target.value)}
              className="ca-input w-full px-3 py-2 text-sm"
            />
          </label>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-3">
        <button
          type="button"
          onClick={() => {
            void handleSubmit()
          }}
          disabled={!changed || saving}
          className="ca-btn-primary inline-flex h-9 items-center px-4 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? savingLabel : saveLabel}
        </button>
        {feedback === 'saved' ? (
          <span className="text-sm text-(--color-status-success)">Dati aggiornati</span>
        ) : feedback === 'error' ? (
          <span className="text-sm text-(--color-status-error)">Aggiornamento non riuscito</span>
        ) : null}
      </div>
    </section>
  )
}
